import type { ActionStatus } from '@/contexts/action-plans/ActionPlans.interfaces'
import type { MeasureStatus } from '@/contexts/security-base/SecurityBase.interfaces'
import type { TranslatedFields } from '@/interfaces/Language.interfaces'
import type {
  DatatableGroupByBase,
  DatatableSortingOrderBy,
  DatatableGroupByOptions,
} from '@/components/NewDesignSystem/NdsDatatable/NdsDatatable.interfaces'
import type { Pagination } from '@/interfaces/Pagination.interfaces'
import type { FilterOperator, FilterPredicates } from '@/contexts/shared/filters-predicate/FiltersPredicate.interfaces'

export type PerimeterId = number

export interface Perimeter {
  id: PerimeterId
  identifier: string
  name: TranslatedFields
}

export enum PerimeterType {
  INTERNAL = 'INTERNAL',
  PROVIDER = 'PROVIDER',
  APPLICATION = 'APPLICATION',
}

export enum PerimeterTypeWithTag {
  TAG = 'TAG',
  INTERNAL = 'INTERNAL',
  PROVIDER = 'PROVIDER',
  APPLICATION = 'APPLICATION',
}

export enum PerimeterTagType {
  ROOT = 'ROOT',
  TAG = 'TAG',
}

export interface PerimetersGroupedItemParent {
  id: number
  name: TranslatedFields
  isRoot: boolean
  treeName: TranslatedFields | null
}

export interface PerimetersGroupedItemRights {
  view: boolean
  edit: boolean
  delete: boolean
  disassociate: boolean
}

export enum PerimetersGroupBy {
  TYPE = 'TYPE',
}

export enum PerimetersSortField {
  IDENTIFIER = 'IDENTIFIER',
  NAME = 'NAME',
  TYPE = 'TYPE',
}

export interface PerimetersGroupedFilter {
  rootPerimeterId?: FilterPredicates<number, FilterOperator.IN>
  parentPerimeterIds?: FilterPredicates<number, FilterOperator.IN>
  types?: FilterPredicates<PerimeterTypeWithTag, FilterOperator.IN>
  search?: FilterPredicates<string, FilterOperator.CONTAINS>
}

export interface PerimetersGroupedBody {
  perimeter_filter: PerimetersGroupedFilter
  sort_field?: PerimetersSortField
  sort_order?: DatatableSortingOrderBy
  group_by?: DatatableGroupByOptions<PerimetersGroupBy>
  pagination?: Pagination
}

export interface PerimetersGroupedItem {
  id: number
  identifier: string
  name: TranslatedFields
  type: PerimeterTypeWithTag
  tagId: number | null
  parents: PerimetersGroupedItemParent[]
  rights: PerimetersGroupedItemRights
}

export type PerimetersGrouped = DatatableGroupByBase<PerimetersGroupedItem>

export interface PerimeterParent {
  id: PerimeterId
  name: TranslatedFields
}

export type PerimeterParentPath = PerimeterParent[]

export interface PerimeterDetails {
  id: PerimeterId
  identifier: string
  name: TranslatedFields
  type: PerimeterTypeWithTag
  description: TranslatedFields
  parentPaths: PerimeterParentPath[]
}

export enum PerimeterPanelTab {
  PROPERTIES = 'PROPERTIES',
  POLICIES = 'POLICIES',
}

export interface PerimeterToAdd {
  type: PerimeterTypeWithTag
  parentPerimeterId: number | null
  identifier: string
  name: TranslatedFields
  description: TranslatedFields
}

export interface PerimeterAction {
  id: number
  status: ActionStatus
}

export interface BeneficiaryPerimeter {
  id: number
  name: TranslatedFields
  identifier?: string
  implementation: {
    status: MeasureStatus
    action?: PerimeterAction
  }
}

export interface PerimeterTreeChildItem {
  child_id: number
  parent_id: number | null
  name: string | null
  weight: number | null
  order: number | null
}

export interface PerimeterTreeTagItem {
  db_id: number
  id: string
  perimeter_id: number
  identifier: string
  name: string | null
  tag: string | null
  type: string
  children_: PerimeterTreeChildItem[]
  parentId: string | null
  has_parent: boolean
  delete_right: boolean
  view_right: boolean
  edit_right: boolean
}

export interface PerimeterTreePerimeterItem {
  db_id: number
  id: string
  identifier: string
  name: string | null
  type: string
  perimeter_type: string | null
  tags: PerimeterTreeTagItem[]
  parentId: string | null
  weight: number | null
  color: string | null
  delete_right: boolean
  view_right: boolean
  edit_right: boolean
  desassociate_right: boolean
  mandatory_input: boolean
}

interface PerimeterTreeSelectOptionBase {
  db_id: number
  id: number
  perimeterId: number
  identifier: string
  name: string
  parentId: string | null
  children?: PerimeterTreeSelectOption[]
}

export interface PerimeterTreeTagSelectOption extends PerimeterTreeSelectOptionBase {
  children_: PerimeterTreeChildItem[]
  tag: string | null
  type: 't'
}

export interface PerimeterTreePerimeterSelectOption extends PerimeterTreeSelectOptionBase {
  perimeter_type: string | null
  type: 'p'
}

export type PerimeterTreeSelectOption = PerimeterTreeTagSelectOption | PerimeterTreePerimeterSelectOption

export type PerimeterTreeItem = PerimeterTreeTagItem | PerimeterTreePerimeterItem

export interface PerimeterTreeOrderingContext {
  customerName: string
  isGlobalPiloteOrAuditor: boolean
  localTagId: number | null
}

export interface PerimeterTree {
  add_right: boolean
  objects: PerimeterTreeTagItem[]
  perimeters: PerimeterTreePerimeterItem[]
  tree_root_node_id: number
}

export interface ImportPerimetersResult {
  message: TranslatedFields
  report: string
}

// Snake case is kept as these objects are consumed as is by TagMixin
export interface UserPerimeterChildTag {
  child_id: number
  name: string
}

export interface UserPerimeterTag {
  id: number
  tag: string
  children_: UserPerimeterChildTag[]
  is_root: boolean
}

export interface UserPerimeter {
  id: PerimeterId
  identifier: string
  name: string
  type: PerimeterType | null
  edit_right: boolean
  view_right: boolean
  tags: UserPerimeterTag[]
}

export interface UserPerimeters {
  perimeters: UserPerimeter[]
  tags: UserPerimeterTag[]
}
